import { memo, useEffect, useLayoutEffect, useRef, useState } from "react"
import { ReactFlowProvider, useReactFlow } from "@xyflow/react"
import { useParams } from "react-router-dom"
import "./System.scss"
import Flow from "../components/Flow"
import ApiFlow from "../components/ApiFlow"
import { Box, VStack } from "@chakra-ui/react"
import {
  useSystemViewData,
  useSystemLoading,
  useSystemError,
  useSystemRefetch,
  useSystemInfo,
  useSystemHeadlines,
} from "../providers/helpers/useSystem"
import SystemHeadlineFigures from "./SystemHeadlineFigures"

/** Keep in step with ApiFlow's `fitViewOptions` — these fits compete on load, so a mismatch would
 *  make the canvas jump between two framings depending on which one lands last. */
const FIT_PADDING = 0.15
const noop = () => { }

const FitMe = ({
  children,
  layoutRevision,
}: {
  children: React.ReactNode
  layoutRevision: number
}) => {
  const reactFlow = useReactFlow()
  const containerRef = useRef<HTMLDivElement>(null)
  const fitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const scheduleFit = () => {
      // Debounce fitView calls to avoid thrashing during layout shifts
      if (fitTimeoutRef.current) clearTimeout(fitTimeoutRef.current)
      fitTimeoutRef.current = setTimeout(() => {
        reactFlow?.fitView({ padding: FIT_PADDING, duration: 200 })
      }, 50)
    }

    // Observe the container for size changes (handles both window resize and layout shifts)
    const el = containerRef.current
    let observer: ResizeObserver | null = null
    if (el) {
      observer = new ResizeObserver(scheduleFit)
      observer.observe(el)
    }

    // Also listen for window resize as a fallback
    window.addEventListener("resize", scheduleFit)

    // Initial fit after nodes render — use rAF chain for reliable timing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reactFlow?.fitView({ padding: FIT_PADDING, duration: 200 })
      })
    })

    return () => {
      window.removeEventListener("resize", scheduleFit)
      observer?.disconnect()
      if (fitTimeoutRef.current) clearTimeout(fitTimeoutRef.current)
    }
  }, [reactFlow, layoutRevision])

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }}>{children}</div>
}

/** Isolated headline component — subscribes to HeadlinesContext without causing parent re-renders */
function SystemHeadlinesSection({ loading }: { loading: boolean }) {
  const headlines = useSystemHeadlines()
  const info = useSystemInfo()
  if (!headlines) return null
  return (
    <SystemHeadlineFigures
      headlines={headlines}
      loading={loading}
      figureGridMb={0}
      figures={info?.figures}
    />
  )
}

/** Memoized ApiFlow wrapper — only re-renders when its own props change */
const MemoizedApiFlow = memo(ApiFlow)

const System = () => {
  const { systemId = "848bd52d-c1cb-4013-8386-4bcb2b7e13a3" } = useParams()
  const containerRef = useRef<HTMLDivElement>(null)
  const headlinesRef = useRef<HTMLDivElement>(null)
  const [measuredHeadlineHeight, setMeasuredHeadlineHeight] = useState(0)

  // Get system data from provider (no headlines subscription here)
  const systemViewData = useSystemViewData()
  const loading = useSystemLoading()
  const error = useSystemError()
  const refetch = useSystemRefetch()

  const stableRefetch = refetch || noop

  // Determine which system to render
  const isHardcodedSystem = systemId === "848bd52d-c1cb-4013-8386-4bcb2b7e13a3"
  const shouldUseApiSystem = !isHardcodedSystem && (systemViewData || loading || error)

  // We still need to know if headlines exist for layout
  // The SystemHeadlinesSection component self-hides when no headline data is available
  const showHeadlinesSlot = shouldUseApiSystem

  useLayoutEffect(() => {
    if (!showHeadlinesSlot) {
      setMeasuredHeadlineHeight(0)
      return
    }
    const el = headlinesRef.current
    if (!el) {
      return
    }
    setMeasuredHeadlineHeight(el.getBoundingClientRect().height)
  }, [showHeadlinesSlot, loading])

  useEffect(() => {
    if (!showHeadlinesSlot) {
      return
    }

    const el = headlinesRef.current
    if (!el) {
      return
    }

    const observer = new ResizeObserver(() => {
      const box = headlinesRef.current
      if (box) {
        setMeasuredHeadlineHeight(box.getBoundingClientRect().height)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [showHeadlinesSlot])

  // The parent `.view-wrapper` is calc(100vh - 60px) with the section heading/nav above us.
  // Use max() so that:
  //   - On tall screens: height = calc(100vh - 140px), fills the space with no scroll
  //   - On short screens: height = 700px (the floor), overflows the parent, triggering scroll

  return (
    <VStack
      width="100%"
      height="max(700px, calc(100vh - 155px))"
      alignItems="stretch"
      gap={0}
    >
      {showHeadlinesSlot && (
        <Box ref={headlinesRef} width="100%" flexShrink={0} pb={2}>
          <SystemHeadlinesSection loading={loading} />
        </Box>
      )}
      <Box
        minWidth="300px"
        width="100%"
        flex="1 1 0px"
        overflow="hidden"
        ref={containerRef}
        bg="card"
        boxShadow={"card"}
        borderRadius={"card"}
      >
        <div className="providerflow"  >
          <ReactFlowProvider>
            <div className="reactflow-wrapper">
              <FitMe layoutRevision={measuredHeadlineHeight}>
                {shouldUseApiSystem ? (
                  <MemoizedApiFlow
                    systemId={systemId}
                    systemData={systemViewData}
                    loading={loading}
                    error={error}
                    refetch={stableRefetch}
                    container={containerRef}
                  />
                ) : (
                  <Flow />
                )}
              </FitMe>
            </div>
          </ReactFlowProvider>
        </div>
      </Box>
    </VStack>
  )
}

export default System
